import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import ReactTable from "src/components/tables/react-table/ReactTable";
import { handleCsvExport } from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useManualRefresh from "src/hooks/useManualRefresh";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { useDeleteWithdraw, useGetWithdraws } from "../api-clients";
import useWithdrawTableColumn from "./useWithdrawTableColumn";

const WithdrawList = () => {
  useSetActiveTab(TAB_NAMES.WITHDRAW);
  const navigate = useNavigate();
  const [length, setLength] = useState(10);

  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);
  const { successNotification, errorNotification } = useNotification();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [idToOperate, setIdToOperate] = useState<number | undefined>(undefined);
  const {
    isLoading,
    error,
    data,
    mutate: reloadWithdrawData,
    isValidating,
  } = useGetWithdraws({ length, page, search: debouncedSearch });
  const { isRefreshing, handleRefresh } = useManualRefresh(reloadWithdrawData);

  const {
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSucceed,
    error: deleteErr,
    execute: deleteWithdraw,
  } = useDeleteWithdraw();

  const withdrawsData = useMemo(() => data?.data?.rows ?? [], [data?.data]);

  const totalRecords = useMemo(() => data?.data?.count, [data?.data?.count]);

  const columns = useWithdrawTableColumn({
    onDelete: (id: number) => {
      setIdToOperate(id);
      setIsDeleteModalOpen(true);
    },
  });

  useEffect(() => {
    if (isDeleteSucceed) {
      reloadWithdrawData();
      setIsDeleteModalOpen(false);
      successNotification("Withdraw Deleted.");
    }
  }, [isDeleteSucceed, successNotification, reloadWithdrawData]);

  useEffect(() => {
    if (deleteErr) {
      errorNotification();
    }
  }, [deleteErr, errorNotification]);

  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        isLoading={isDeleteLoading}
        onSubmit={() => deleteWithdraw(idToOperate)}
      />
      <div className="flex flex-col gap-[20px]">
        <TabHeader
          label="Withdraw"
          onRefresh={() => handleRefresh()}
          exportButton={{
            label: "Export",
            onClick: () => handleCsvExport(withdrawsData),
          }}
          printButton={{
            label: "Print",
            onClick: () => window.print(),
          }}
          createButton={{
            label: "Add Withdraw",
            onClick: () => navigate("create"),
          }}
          search={{
            onChange: (val) => setSearchText(val),
            value: searchText,
          }}
        />
        <ReactTable
          columns={columns}
          data={withdrawsData}
          isLoading={isLoading || isRefreshing}
          pagination={{
            setLength,
            length,
            total: totalRecords,
            page,
            setPage,
          }}
        />
      </div>
    </>
  );
};

export default WithdrawList;
